import { getNestConnection, requestNestHTTP } from "./client"
import type { Attachment } from "./generated"

export type NestAttachment = Attachment

export interface NestAttachmentDownload {
    data: ArrayBuffer
    mediaType: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function parseAttachment(value: unknown): NestAttachment | null {
    if (!isRecord(value)) return null
    if (
        typeof value.attachmentId !== "string" ||
        typeof value.sha256 !== "string" ||
        typeof value.sizeBytes !== "number" ||
        typeof value.createdAt !== "string"
    ) {
        return null
    }
    return {
        attachmentId: value.attachmentId,
        sha256: value.sha256,
        sizeBytes: value.sizeBytes,
        createdAt: value.createdAt,
        ...(typeof value.mediaType === "string" ? { mediaType: value.mediaType } : {}),
        ...(typeof value.filename === "string" ? { filename: value.filename } : {}),
    }
}

function attachmentPath(attachmentId?: string, filename?: string): string {
    if (attachmentId) return `/v1/attachments/${encodeURIComponent(attachmentId)}`
    const url = new URL("http://nest.invalid/v1/attachments")
    if (filename) url.searchParams.set("filename", filename)
    return `${url.pathname}${url.search}`
}

function arrayBufferFromBytes(bytes: Uint8Array): ArrayBuffer {
    return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
}

async function readErrorMessage(response: Response): Promise<string> {
    try {
        const value = await response.json()
        if (isRecord(value) && typeof value.error === "string") return value.error
    } catch {
        // Fall through to the HTTP status text.
    }
    return response.statusText || `HTTP ${response.status}`
}

export async function uploadNestAttachment(
    data: Blob | ArrayBuffer | Uint8Array,
    options: { mediaType?: string; filename?: string } = {}
): Promise<NestAttachment> {
    const connection = await getNestConnection()
    const body = data instanceof Uint8Array ? arrayBufferFromBytes(data) : data
    const mediaType = options.mediaType ?? (data instanceof Blob ? data.type : "")
    const headers = new Headers({ Authorization: `Bearer ${connection.token}` })
    if (mediaType) headers.set("Content-Type", mediaType)

    const response = await requestNestHTTP(connection, attachmentPath(undefined, options.filename), {
        method: "POST",
        headers,
        body,
    })
    if (!response.ok) throw new Error(`Attachment upload failed: ${await readErrorMessage(response)}`)
    const payload: unknown = await response.json()
    const attachment = isRecord(payload) ? parseAttachment(payload.attachment) : null
    if (!attachment) throw new Error("Attachment upload returned an invalid response")
    return attachment
}

export async function downloadNestAttachment(attachmentId: string): Promise<NestAttachmentDownload> {
    const connection = await getNestConnection()
    const response = await requestNestHTTP(connection, attachmentPath(attachmentId), {
        method: "GET",
        headers: { Authorization: `Bearer ${connection.token}` },
    })
    if (!response.ok) throw new Error(`Attachment download failed: ${await readErrorMessage(response)}`)
    return {
        data: await response.arrayBuffer(),
        mediaType: response.headers.get("Content-Type") ?? "application/octet-stream",
    }
}

export async function downloadNestAttachmentRange(attachmentId: string, start: number, endExclusive: number): Promise<NestAttachmentDownload> {
    if (!Number.isInteger(start) || !Number.isInteger(endExclusive) || start < 0 || endExclusive <= start) {
        throw new Error("Invalid attachment range")
    }
    const connection = await getNestConnection()
    const response = await requestNestHTTP(connection, attachmentPath(attachmentId), {
        method: "GET",
        headers: {
            Authorization: `Bearer ${connection.token}`,
            Range: `bytes=${start}-${endExclusive - 1}`,
        },
    })
    if (!response.ok) throw new Error(`Attachment download failed: ${await readErrorMessage(response)}`)
    return {
        data: await response.arrayBuffer(),
        mediaType: response.headers.get("Content-Type") ?? "application/octet-stream",
    }
}
